import { memo, useMemo, useState, ChangeEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import styled, { css } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import isEqual from "lodash.isequal";
import groupBy from "lodash.groupby";

import { MinimalChannelResource } from "@/types/resources";
import { api } from "@/utils/api";
import useDidMount from "@/hooks/useDidMount";
import { Dispatch, RootState } from "@/utils/store";
import Button from "@/components/Button";
import Text from "@/components/Text";
import { Input } from "@/components/Forms";
import { selectDistributionChannelsFilter } from "@/selectors";
import { ensureStringContainsPositiveDigitsOnly } from "@/utils/helpers";

const Card = styled.div<{ $opened: boolean }>`
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  margin: 0 0 16px;
  position: relative;

  ${({ $opened }) =>
    $opened &&
    css`
      border-bottom-color: transparent;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      box-shadow: 0px 3px 4px rgb(0 0 0 / 3%);
    `}
`;

const Header = styled.div`
  padding: 20px 24px;
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const BottomActionsHolder = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 8px 0;
`;

const ToggleButton = styled.button<{ $rotate: boolean }>`
  flex-shrink: 0;
  margin-left: 90px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  transition: 0.2s ease-out border-color, 0.2s ease-out transform;

  &:hover {
    border-color: ${({ theme }) => theme.colors.black};
  }

  ${({ $rotate }) =>
    $rotate &&
    css`
      transform: rotate(180deg);
    `}
`;

const HeaderContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeaderChannels = styled(Text)`
  margin-top: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledButton = styled(Button)`
  font-size: 14px;
  min-width: 120px;
  height: 38px;
  padding: 0 20px;
`;

const ApplyButton = styled(StyledButton)`
  margin-left: 16px;
`;

const Body = styled.div`
  position: absolute;
  max-height: 500px;
  overflow-y: auto;
  z-index: ${({ theme }) => theme.zIndex[2]};
  left: -1px;
  top: 100%;
  width: calc(100% + 2px);
  box-shadow: 0px 3px 4px rgba(0, 0, 0, 0.03);
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-top-color: ${({ theme }) => theme.colors.lightGray};
  padding: 24px;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-row-gap: 24px;
`;

const Title = styled(Text).attrs({
  $type: "smallBold",
  $color: "gray",
  as: "h5",
})`
  margin-bottom: 12px;
  text-transform: uppercase;
`;

const InnerCard = styled.div`
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-height: 64px;
`;

const SelectedChannelsHeader = styled.div`
  display: flex;
  align-items: baseline;
  margin-bottom: 12px;
  ${Title} {
    margin-bottom: 0;
    margin-right: auto;
  }
`;

const ChannelListButton = styled(Text).attrs({
  as: "button",
  $type: "smallBold",
})`
  height: 30px;
  display: inline-flex;
  padding: 0 12px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  border: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  border-radius: 4px;
  align-items: center;
  transition: 0.2s ease-out color, 0.2s ease-out border-color;

  &:hover {
    border-color: ${({ theme }) => theme.colors.purple};
    color: ${({ theme }) => theme.colors.purple};
  }

  ${({ theme, $color }) =>
    $color === "purple" &&
    css`
      border-color: ${theme.colors.purple};
      color: ${theme.colors.purple};
    `}
`;

const EmptyListTitle = styled(Text).attrs({
  $color: "gray",
  $type: "smallBold",
})`
  text-align: center;
  width: 100%;
`;

const CheckIcon = styled.svg`
  margin-right: 8px;
`;

const SelectedChannelsAction = styled(Text).attrs({
  $type: "smallBold",
})`
  margin-left: 24px;
  transition: 0.2s ease-out opacity;
  &:not([disabled]):hover {
    opacity: 0.7;
  }
  &:disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.colors.gray};
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
`;

const MarginedSectionHeader = styled(SectionHeader)`
  margin-top: 8px;
`;

const SectionHeaderLine = styled.div`
  flex: 1;
  margin-left: 24px;
  height: 1px;
  background: ${({ theme }) => theme.colors.grayscale};
  margin-top: 3px;
`;

type SelectionType = "from" | "to";

interface SelectedChannel extends MinimalChannelResource {
  type: SelectionType;
}

const DistributionChannelsFilter = () => {
  const [isOpened, setIsOpened] = useState(false);

  const [selectedChannels, setSelectedChannels] = useState<SelectedChannel[]>(
    []
  );

  const [lastReleaseDays, setLastReleaseDays] = useState<{
    [key in SelectionType]: string;
  }>({
    from: "",
    to: "",
  });

  const { teamId } = useParams();

  const channelFilterAttributes = useSelector((rootState: RootState) =>
    selectDistributionChannelsFilter(rootState, teamId!)
  );

  const dispatch = useDispatch<Dispatch>();

  const {
    data: channels,
    isError,
    isLoading,
  } = useQuery<MinimalChannelResource[]>(
    ["channels", { teamId, competitors: 0, minimal: true }],
    () =>
      api.get<never, MinimalChannelResource[]>(
        `/v1/teams/${teamId}/channels/list`,
        {
          params: {
            competitors: 0,
          },
        }
      ),
    {
      staleTime: 1000 * 120,
    }
  );

  const filteredChannels = useMemo(() => {
    const selectedChannelsIds = selectedChannels.map((c) => c.id);

    return channels?.filter((channel) => {
      if (selectedChannelsIds.includes(channel.id)) {
        return false;
      }

      return true;
    });
  }, [channels, selectedChannels]);

  const groupedChannels = useMemo(
    () => groupBy(selectedChannels, (c) => c.type),
    [selectedChannels]
  ) as {
    from?: SelectedChannel[];
    to?: SelectedChannel[];
  };

  const onRemoveSelectedChannel = (channelId: number) => {
    setSelectedChannels((prevChannels) =>
      prevChannels.filter((channel) => channel.id !== channelId)
    );
  };

  const clearSelections = (type: SelectionType) => {
    setSelectedChannels((prev) => prev.filter((c) => c.type !== type));
  };

  const onLastReleaseChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (!ensureStringContainsPositiveDigitsOnly(value)) {
      return;
    }

    setLastReleaseDays((prev) => ({ ...prev, [name as SelectionType]: value }));
  };

  const onCancel = () => {
    setSelectedChannels([]);
    setIsOpened(false);
    dispatch.distribution.resetProjectsChannelsFilter(teamId!);
  };

  const ensureFilterValuesChanged = () => {
    return (
      !isEqual(selectedChannels, [
        ...(channelFilterAttributes?.channelsFrom.map(
          (c) =>
            ({
              ...c,
              type: "from",
            } as SelectedChannel)
        ) ?? []),
        ...(channelFilterAttributes?.channelsTo.map(
          (c) =>
            ({
              ...c,
              type: "to",
            } as SelectedChannel)
        ) ?? []),
      ]) ||
      !isEqual(lastReleaseDays, {
        from: channelFilterAttributes?.lastReleaseFrom,
        to: channelFilterAttributes?.lastReleaseTo,
      })
    );
  };

  const onApply = () => {
    if (!ensureFilterValuesChanged()) {
      return setIsOpened(false);
    }

    dispatch.distribution.setProjectsChannelsFilter({
      teamId: teamId!,
      attributes: {
        channelsFrom: groupedChannels.from ?? [],
        channelsTo: groupedChannels.to ?? [],
        lastReleaseFrom: lastReleaseDays.from,
        lastReleaseTo: lastReleaseDays.to,
      },
    });
    setIsOpened(false);
  };

  const onToggle = () => {
    if (!isOpened) {
      return setIsOpened(true);
    }

    if (!ensureFilterValuesChanged()) {
      return setIsOpened(false);
    }

    dispatch.distribution.setProjectsChannelsFilter({
      teamId: teamId!,
      attributes: {
        channelsFrom: groupedChannels.from ?? [],
        channelsTo: groupedChannels.to ?? [],
        lastReleaseFrom: lastReleaseDays.from,
        lastReleaseTo: lastReleaseDays.to,
      },
    });

    setIsOpened(false);
  };

  useDidMount(() => {
    if (channelFilterAttributes) {
      setSelectedChannels([
        ...channelFilterAttributes.channelsFrom.map(
          (c) =>
            ({
              ...c,
              type: "from",
            } as SelectedChannel)
        ),
        ...channelFilterAttributes.channelsTo.map(
          (c) =>
            ({
              ...c,
              type: "to",
            } as SelectedChannel)
        ),
      ]);
      setLastReleaseDays({
        from: channelFilterAttributes.lastReleaseFrom,
        to: channelFilterAttributes.lastReleaseTo,
      });
    }
  });

  if (isLoading || isError) return null;

  return (
    <Card $opened={isOpened}>
      <Header onClick={onToggle}>
        <HeaderContent>
          <Text $type="button" as="div">
            Channels{" "}
            <Text $type="button" $color="gray" as="span">
              ({selectedChannels.length || channels?.length})
            </Text>
          </Text>
          {!!selectedChannels?.length && (
            <HeaderChannels $type="smallBold" $color="gray">
              {selectedChannels?.map((channel) => channel.name).join(" / ")}
            </HeaderChannels>
          )}
        </HeaderContent>
        <ToggleButton $rotate={isOpened}>
          <svg
            width="8"
            height="6"
            viewBox="0 0 8 6"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M4 5.5L8 0.5H0L4 5.5Z" fill="currentColor" />
          </svg>
        </ToggleButton>
      </Header>
      {isOpened && (
        <Body>
          <SectionHeader>
            <Text $type="button">Filter from</Text>
            <SectionHeaderLine />
          </SectionHeader>
          <div>
            <SelectedChannelsHeader>
              <Title>Selected Channels</Title>
              <SelectedChannelsAction
                as="button"
                onClick={() => clearSelections("from")}
                disabled={selectedChannels.length === 0}
              >
                Clear
              </SelectedChannelsAction>
            </SelectedChannelsHeader>
            <InnerCard>
              {!!groupedChannels.from?.length ? (
                groupedChannels.from.map((channel) => (
                  <ChannelListButton
                    key={channel.id}
                    $color="purple"
                    onClick={() => onRemoveSelectedChannel(channel.id)}
                  >
                    <CheckIcon
                      width="13"
                      height="9"
                      viewBox="0 0 13 9"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 1L4.66663 8L1 4.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </CheckIcon>

                    {channel.name}
                  </ChannelListButton>
                ))
              ) : (
                <EmptyListTitle>No Channels Selected</EmptyListTitle>
              )}
            </InnerCard>
          </div>
          <div>
            <Title>Channels List</Title>
            <InnerCard>
              {filteredChannels?.length ? (
                filteredChannels?.map((channel) => (
                  <ChannelListButton
                    key={channel.id}
                    onClick={() =>
                      setSelectedChannels((prev) => [
                        ...prev,
                        { type: "from", ...channel },
                      ])
                    }
                  >
                    {channel.name}
                  </ChannelListButton>
                ))
              ) : (
                <EmptyListTitle>No Channels To Select</EmptyListTitle>
              )}
            </InnerCard>
          </div>
          <div>
            <Title>Last release (days)</Title>
            <Input
              placeholder="Add days"
              name="from"
              value={lastReleaseDays.from}
              onChange={onLastReleaseChange}
            />
          </div>
          <MarginedSectionHeader>
            <Text $type="button">Target to</Text>
            <SectionHeaderLine />
          </MarginedSectionHeader>
          <div>
            <SelectedChannelsHeader>
              <Title>Selected Channels</Title>
              <SelectedChannelsAction
                as="button"
                onClick={() => clearSelections("to")}
                disabled={selectedChannels.length === 0}
              >
                Clear
              </SelectedChannelsAction>
            </SelectedChannelsHeader>
            <InnerCard>
              {!!groupedChannels.to?.length ? (
                groupedChannels.to.map((channel) => (
                  <ChannelListButton
                    key={channel.id}
                    $color="purple"
                    onClick={() => onRemoveSelectedChannel(channel.id)}
                  >
                    <CheckIcon
                      width="13"
                      height="9"
                      viewBox="0 0 13 9"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 1L4.66663 8L1 4.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </CheckIcon>

                    {channel.name}
                  </ChannelListButton>
                ))
              ) : (
                <EmptyListTitle>No Channels Selected</EmptyListTitle>
              )}
            </InnerCard>
          </div>
          <div>
            <Title>Channels List</Title>
            <InnerCard>
              {filteredChannels?.length ? (
                filteredChannels?.map((channel) => (
                  <ChannelListButton
                    key={channel.id}
                    onClick={() =>
                      setSelectedChannels((prev) => [
                        ...prev,
                        { type: "to", ...channel },
                      ])
                    }
                  >
                    {channel.name}
                  </ChannelListButton>
                ))
              ) : (
                <EmptyListTitle>No Channels To Select</EmptyListTitle>
              )}
            </InnerCard>
          </div>
          <div>
            <Title>Last release (days)</Title>
            <Input
              placeholder="Add days"
              name="to"
              value={lastReleaseDays.to}
              onChange={onLastReleaseChange}
            />
          </div>
          <BottomActionsHolder>
            <StyledButton onClick={onCancel} color="secondary">
              Cancel
            </StyledButton>
            <ApplyButton $color="action" onClick={onApply}>
              Apply filter
            </ApplyButton>
          </BottomActionsHolder>
        </Body>
      )}
    </Card>
  );
};

export default memo(DistributionChannelsFilter);
